/**
 * The Shelves tab of the Products page (owner ruling N-54, 2026-09-17: "Shelves =
 * secondary tab per country"). Lifted out of `ProductsPage.tsx` unchanged in copy and
 * in control names — `products-page.test.tsx` addresses it by 'New shelf', 'Add shelf',
 * 'Rename', 'Rename <name>', 'Save', 'Delete' and the disabled-with-reason title — so
 * the move is a change of address, not of behaviour.
 *
 * Shelves are absorbed on the catalogue screen per the owner's ruling (OD-A): a shelf
 * has no life apart from the catalogue it files. Single-country only: the ALL view says
 * where to go instead of showing six countries' shelves at once. Inline rename replaced
 * `window.prompt` (§2.5), and delete is DISABLED WITH A REASON while products are filed
 * — the server refuses regardless and the refusal renders verbatim through
 * `actionError`.
 */

import { useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { Button, EmptyState, SkeletonRows, TextField } from '../../components/ui';
import type { CountryCode } from '../../domain/countries';
import type { ProductGroupDto } from './catalogue-api';
import { errorSentence } from './states';

/** The three shelf writes, as one discriminated union so the mutation has one input type. */
export type ShelfAction =
  | { kind: 'create'; name: string }
  | { kind: 'rename'; id: string; name: string }
  | { kind: 'delete'; id: string };

export function ShelvesPanel({
  viewCc,
  groups,
  busy,
  onAction,
}: {
  /** `null` is the interleaved ALL view, which maintains no shelves. */
  viewCc: CountryCode | null;
  groups: UseQueryResult<ProductGroupDto[]>;
  busy: boolean;
  /** Fires the write; `done` runs on success so the panel can clear its own draft. */
  onAction: (action: ShelfAction, done: () => void) => void;
}): React.JSX.Element {
  const [newShelf, setNewShelf] = useState('');
  /** Inline shelf rename — the state that replaced `window.prompt`. */
  const [shelfEdit, setShelfEdit] = useState<{ id: string; value: string } | null>(null);

  if (viewCc === null) {
    return (
      <p className="text-label text-fg-secondary">
        Shelves belong to one country’s catalogue. Pick a country above to file its products
        or maintain its shelves.
      </p>
    );
  }
  if (groups.isPending) return <SkeletonRows rows={3} label="Loading shelves…" />;
  if (groups.isError) {
    return (
      <EmptyState
        title="Could not load the shelves"
        description={errorSentence(groups.error, 'The product groups could not be read.')}
      />
    );
  }
  const shelfRows = groups.data;

  return (
    <div className="space-y-4">
      <p className="text-label text-fg-secondary">
        How {viewCc} files its own catalogue. Shelves are for finding things and for the
        headings on a quotation — they never change a price, a tax or what a quote is
        allowed to contain.
      </p>

      {shelfRows.length === 0 ? (
        <EmptyState
          title="No shelves yet"
          description={`Products in ${viewCc} list in one flat catalogue, and quotations print flat. Add a shelf to file them.`}
        />
      ) : (
        <ul className="divide-y divide-line">
          {shelfRows.map((group) => (
            <li
              key={group.id}
              className="flex flex-wrap items-center justify-between gap-3 py-3"
            >
              {shelfEdit?.id === group.id ? (
                <form
                  className="flex grow flex-wrap items-end gap-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const next = shelfEdit.value.trim();
                    if (next === '' || next === group.name) {
                      setShelfEdit(null);
                      return;
                    }
                    onAction({ kind: 'rename', id: group.id, name: next }, () => {
                      setShelfEdit(null);
                    });
                  }}
                >
                  <div className="min-w-48 grow">
                    <TextField
                      label={`Rename ${group.name}`}
                      value={shelfEdit.value}
                      onChange={(event) => {
                        setShelfEdit({ id: group.id, value: event.target.value });
                      }}
                    />
                  </div>
                  <Button type="submit" disabled={busy}>
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="quiet"
                    onClick={() => {
                      setShelfEdit(null);
                    }}
                  >
                    Cancel
                  </Button>
                </form>
              ) : (
                <>
                  <div className="min-w-0">
                    <p className="text-body text-fg">{group.name}</p>
                    <p className="text-label text-fg-secondary">
                      {group.product_count === 0
                        ? 'Nothing filed here'
                        : `${String(group.product_count)} product${group.product_count === 1 ? '' : 's'}`}
                      {group.active ? '' : ' · retired'}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button
                      variant="quiet"
                      disabled={busy}
                      onClick={() => {
                        setShelfEdit({ id: group.id, value: group.name });
                      }}
                    >
                      Rename
                    </Button>
                    {/*
                     * ⚠ DISABLED WITH A REASON, NOT HIDDEN — the seat MAY delete, just not
                     * while products are on the shelf. The server refuses regardless and
                     * `actionError` renders that 409 verbatim: the count here can be
                     * stale, and the truth is the refusal.
                     */}
                    <Button
                      variant="quiet"
                      disabled={busy || group.product_count > 0}
                      title={
                        group.product_count > 0
                          ? 'Move its products to another shelf first — deleting it would leave them with no record of where they were.'
                          : undefined
                      }
                      onClick={() => {
                        onAction({ kind: 'delete', id: group.id }, () => undefined);
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (newShelf.trim() === '') return;
          onAction({ kind: 'create', name: newShelf.trim() }, () => {
            setNewShelf('');
          });
        }}
      >
        <div className="min-w-56 grow">
          <TextField
            label="New shelf"
            value={newShelf}
            onChange={(event) => {
              setNewShelf(event.target.value);
            }}
            placeholder="Addon"
          />
        </div>
        <Button type="submit" disabled={busy || newShelf.trim() === ''}>
          Add shelf
        </Button>
      </form>
    </div>
  );
}
